import { FCWithChildren } from '@types';
import React, { Fragment, useState } from 'react';

import className from 'classnames';

import { Button, StatusInfoModal, Switch } from '@components';
import useConnectionStatus from '@hooks/useConnectionStatus';
import useDevices from '@hooks/useDevices';
import useLayoutContext from '@hooks/useLayoutContext';

import MobileDevicesList from './MobileDevicesList';
import WebDevicesList from './WebDevicesList';
import { AiOutlineReload } from 'react-icons/ai';

type DevicesListProps = {
  addDeviceAnchorHandler: () => void;
  showFooter?: boolean;
  title?: string;
};

const DevicesList: FCWithChildren<DevicesListProps> = ({
  addDeviceAnchorHandler,
  showFooter,
  title,
}) => {
  const [isStatusInfoModalOpen, setIsStatusInfoModalOpen] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const { data = [], isFetching, refetch } = useDevices();
  const { isMobile } = useLayoutContext();

  const { data: statusDiagnostics = [] } = useConnectionStatus();

  const sectionCls = className(
    'flex flex-col items-center',
    isMobile ? ' -mx-4 w-screen' : ' w-full',
  );
  const divCls = className(
    'sm:-mx-6 lg:-mx-8 w-full',
    isMobile ? ' mt-4 border-t boder-gray-400' : ' -mt-2 mb-2 py-2',
  );

  const closeStatusInfoModal = () => {
    setIsStatusInfoModalOpen(false);
    setDeviceStatus('');
  };

  const openStatusInfoModal = (status: string) => {
    setIsStatusInfoModalOpen(true);
    setDeviceStatus(status);
  };

  const diagnostic = statusDiagnostics.find(({ value }) => value === deviceStatus);

  return (
    <Fragment>
      <StatusInfoModal
        closeModal={closeStatusInfoModal}
        isOpen={isStatusInfoModalOpen}
        statusDiagnostic={diagnostic}
      />
      <div className="flex justify-between">
        <div className="flex items-center gap-4 mb-2">
          {!!title && <h2>{title}</h2>}
          <Button
            layout="link"
            size="noPadding"
            onClick={refetch}
            aria-label="Reload"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-blue-700"
          >
            <AiOutlineReload className="text-xl" />
          </Button>
        </div>
        <Switch.Group>
          <Switch.Label>Active Only</Switch.Label>
          <Switch checked={showActiveOnly} onChange={setShowActiveOnly} />
        </Switch.Group>
      </div>
      <section className={sectionCls}>
        <div className={divCls}>
          <WebDevicesList
            addDeviceAnchorHandler={addDeviceAnchorHandler}
            devices={data}
            loading={isFetching}
            openStatusInfoModal={openStatusInfoModal}
            refresh={refetch}
            showFooter={showFooter}
            showActiveOnly={showActiveOnly}
          />
        </div>
      </section>
    </Fragment>
  );
};

export default DevicesList;
